import { OfferData } from '@/@types/Products/Offers';
import { api } from './api';
import {
  CreateCheckoutRequest,
  EditCheckoutRequest,
} from '@/@types/Checkouts/create-checkout';
import { Checkout, CheckoutsResponse } from '@/@types/Checkouts/list-checkouts';
import { EditProductRequest } from '@/@types/Products';
import { CustomizeCheckoutData } from '@/@types/Checkouts/edit-checkout';
import { formatOfferOptionLabel } from '@/utils/formatOfferRecurringLabel';
import { prepareCustomizeCheckoutPayload } from '@/utils/customizeCheckout';
import { formatCheckoutLabel } from '@/utils/checkoutTemplates';

export interface Options {
  value: string;
  label: string;
}

async function createCheckout({
  data,
  productId,
}: {
  data: CreateCheckoutRequest;
  productId: string;
}) {
  await api.post(`/v1/products/${productId}/checkouts`, data);
}

async function customizeCheckout({
  data,
  productId,
  checkoutId,
}: {
  data: CustomizeCheckoutData;
  productId: string;
  checkoutId: string;
}) {
  const response = await api.post(
    `/v1/products/${productId}/checkouts/${checkoutId}/customize`,
    prepareCustomizeCheckoutPayload(data),
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data.data;
}

async function getOffers({
  productId,
  page,
}: {
  productId: string;
  page: number;
}): Promise<Options[]> {
  const response = await api.get(
    `/v1/products/${productId}/offers?page=${page}`
  );

  const offersOptions = (response.data.data as OfferData[]).map((offer) => ({
    value: offer.id,
    label: formatOfferOptionLabel(offer),
  }));

  return offersOptions;
}

async function getCheckouts({
  productId,
  page,
}: {
  productId: string;
  page: number;
}): Promise<CheckoutsResponse> {
  const response = await api.get(
    `/v1/products/${productId}/checkouts?page=${page}`
  );

  return response.data;
}

async function getCheckoutsByProducts({
  productIds,
  query,
}: {
  productIds: string[];
  query?: string;
}): Promise<CheckoutsResponse | any> {
  const params = new URLSearchParams();

  if (productIds && productIds.length > 0) {
    params.append('products', productIds.join(','));
  }

  // params.append('page', page.toString());

  if (query) {
    params.append('q', query);
  }

  const response = await api.get(
    `/v1/checkouts/by-products?${params.toString()}`
  );

  const checkouts = (
    response.data as Array<{ uuid: string; name: string; template?: string | null }>
  ).map((checkout) => {
    const value = checkout.uuid;
    const label = formatCheckoutLabel(checkout.name, checkout.template);

    return { value, label };
  });

  return checkouts;
}

async function editOffer({
  checkoutId,
  data,
}: {
  checkoutId: string;
  data: EditProductRequest;
}) {
  await api.put(`/v1/products/${checkoutId}/update`, data);
}

async function updateCheckout({
  checkoutId,
  data,
  productId,
}: {
  checkoutId: string;
  data: EditCheckoutRequest;
  productId: string;
}) {
  await api.put(`/v1/products/${productId}/checkouts/${checkoutId}`, data);
}

async function deleteOffer({
  offerId,
  productId,
}: {
  productId: string;
  offerId: string;
}) {
  await api.delete(`/v1/products/${productId}/offers/${offerId}`);
}

async function getCheckout({
  productId,
  checkoutId,
}: {
  productId: string;
  checkoutId: string;
}): Promise<Checkout> {
  const response = await api.get(
    `/v1/products/${productId}/checkouts/${checkoutId}`
  );

  return response.data.data;
}
export const CheckoutsService = {
  createCheckout,
  getCheckouts,
  getOffers,
  editOffer,
  updateCheckout,
  getCheckout,
  customizeCheckout,
  getCheckoutsByProducts,
};
